import FileUpload from '@/components/FileUpload/FileUpload';
import { handleFileRejections, showToast } from '@/lib/functions/funcUtils';
import DocumentUploadUpward from '@/components/ui/icons/new/DocumentUploadUpward';
import { FC, useState } from 'react';
import { cn } from '@/lib/utils';
import { UploadedDocumentsTable } from '@/components/Tables/UploadedDocumentsTable/UploadedDocumentsTable';
import { columns } from '@/components/Tables/UploadedDocumentsTable/columns';
import { FileWithPath } from 'react-dropzone';
import { cloneDeep } from 'lodash';

interface AdditionalDocumentCardProps {
	onChange: (files: File[] | null) => void;
}
const AdditionalDocumentCard: FC<AdditionalDocumentCardProps> = ({
	onChange,
}) => {
	const [additionalFiles, setAdditionalFiles] = useState<File[] | null>(null);

	const handleAdditionalFiles = async (files: File[]) => {
		if (!files.length) return;
		const file: FileWithPath = files[0];
		if (
			additionalFiles &&
			additionalFiles.find(
				(addtionalFile: FileWithPath) => addtionalFile.path === file.path
			)
		) {
			showToast('error', 'Selected document has already been uploaded.');
		} else {
			additionalFiles?.push(file);
			const attachedFiles = additionalFiles
				? cloneDeep(additionalFiles)
				: files;
			setAdditionalFiles(attachedFiles);
			onChange(attachedFiles);
		}
	};

	const handleDetachDocument = (documentPath: string) => {
		if (!additionalFiles) return;
		const fileIndex = additionalFiles.findIndex(
			(addtionalFile: FileWithPath) => addtionalFile.path === documentPath
		);
		additionalFiles.splice(fileIndex, 1);
		setAdditionalFiles(cloneDeep(additionalFiles));
		onChange(additionalFiles);
	};

	const renderUploadLayout = (disabled?) => {
		return (
			<div
				className={cn(
					'flex p-4 2xl:p-6 bg-[#EAF1FC] rounded-2xl border border-[#D3DDE2] gap-3 2xl:gap-4',
					disabled ? 'opacity-50 cursor-ga' : 'cursor-pointer '
				)}
			>
				<DocumentUploadUpward
					className="self-center w-6 h-8"
					stroke="#5D9BFD"
					fill="transparent"
				/>
				<div className="flex flex-col gap-1">
					<h4 className="font-bold text-primary dynamic-medium">
						Import your document
					</h4>
					<p className="text-black">
						This can be a PDF or a word document. Click here or click to specify
						its location
					</p>
				</div>
			</div>
		);
	};

	return (
		<div className="flex flex-col gap-4 p-6 bg-white 2xl:gap-6 rounded-2xl">
			<div className="flex flex-col gap-3 2xl:gap-4">
				<h2 className="font-bold text-black dynamic-large">
					Are there documents you may want to attach?
				</h2>
				<p className="text-black">
					These could be policy documents. Please don't include documents that
					may help produce the proposal.
				</p>
			</div>
			{!additionalFiles?.length || additionalFiles.length < 3 ? (
				<FileUpload
					onDropRejected={(fileRejections) =>
						handleFileRejections({
							fileRejections: fileRejections,
							fileSize: '2 MB',
						})
					}
					onDropAccepted={handleAdditionalFiles}
					type="custom"
					customLayout={renderUploadLayout()}
					maxSize={2000000}
				/>
			) : (
				<>{renderUploadLayout(true)}</>
			)}
			{!!additionalFiles?.length && additionalFiles.length > 0 && (
				<div className="shadow-main rounded-xl">
					<UploadedDocumentsTable
						columns={columns(handleDetachDocument)}
						data={(additionalFiles as any) || []}
						showHeader={true}
					/>
				</div>
			)}
		</div>
	);
};

export default AdditionalDocumentCard;
